/**
 * B060030 受注生産品店別組立出荷リスト
 */
import {
  SText,
  SCodeSelect,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNumberField,
  STextField,
  msgState,
} from "components";
import { B060030HakkoKbn, getSCode, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { FormProvider, useForm } from "react-hook-form";
import { date } from "services/DateService";
import {
  B000111KakoshubetsuNmFrom,
  B000111KakoshubetsuNmTo,
  B000150TokuiNmFrom,
  B000150TokuiNmTo,
  B000151NonyuNmFrom,
  B000151NonyuNmTo,
  B060030Print,
  B060030Request,
  catchFileError,
} from "clients/rest";
import { focusState, useFocus } from "states";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useEffect } from "react";
import { B000111, B000150, B000151 } from "pages";
import { meishoKbn } from "services/codes";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { download, printed } from "services";
import { getClientMessage } from "messages";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  hakkoKbn: SCode;
  shukaYmdFrom: string;
  shukaYmdTo: string;
  seisakuYmdFrom: string;
  seisakuYmdTo: string;
  juchudenpyoNoFrom: string;
  juchudenpyoNoTo: string;
  kakoshubetsuCdFrom: string;
  kakoshubetsuNmFrom: string;
  kakoshubetsuCdTo: string;
  kakoshubetsuNmTo: string;
  tokuiCdFrom: string;
  tokuishitenCdFrom: string;
  tokuiNmFrom: string;
  tokuiCdTo: string;
  tokuishitenCdTo: string;
  tokuiNmTo: string;
  nonyuCdFrom: string;
  nonyushitenCdFrom: string;
  nonyuNmFrom: string;
  nonyuCdTo: string;
  nonyushitenCdTo: string;
  nonyuNmTo: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  hakkoKbn: getSCode(B060030HakkoKbn, "0"),
  shukaYmdFrom: date(),
  shukaYmdTo: date(),
  seisakuYmdFrom: date(),
  seisakuYmdTo: date(),
  juchudenpyoNoFrom: "",
  juchudenpyoNoTo: "",
  kakoshubetsuCdFrom: "",
  kakoshubetsuNmFrom: "",
  kakoshubetsuCdTo: "",
  kakoshubetsuNmTo: "",
  tokuiCdFrom: "",
  tokuishitenCdFrom: "",
  tokuiNmFrom: "",
  tokuiCdTo: "",
  tokuishitenCdTo: "",
  tokuiNmTo: "",
  nonyuCdFrom: "",
  nonyushitenCdFrom: "",
  nonyuNmFrom: "",
  nonyuCdTo: "",
  nonyushitenCdTo: "",
  nonyuNmTo: "",
};

const focusId = "B060030";

export const B060030 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));

  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  //初期表示
  useEffect(() => {
    focus(1);
  });

  //印刷イベント
  const handlePrint = () => {
    // 印刷フォームのデータを取得する。
    const fields = methods.getValues();
    const param: B060030Request = {
      kumitatelisthakozumiKbn: fields.hakkoKbn?.value!,
      shukayoteiYmdFrom: fields.shukaYmdFrom,
      shukayoteiYmdTo: fields.shukaYmdTo,
      seisakuYmdFrom: fields.seisakuYmdFrom,
      seisakuYmdTo: fields.seisakuYmdTo,
      juchudenpyoNoFrom: fields.juchudenpyoNoFrom,
      juchudenpyoNoTo: fields.juchudenpyoNoTo,
      kakoshubetsuCdFrom: fields.kakoshubetsuCdFrom,
      kakoshubetsuCdTo: fields.kakoshubetsuCdTo,
      tokuiCdFrom: fields.tokuiCdFrom,
      tokuishitenCdFrom: fields.tokuishitenCdFrom,
      tokuiCdTo: fields.tokuiCdTo,
      tokuishitenCdTo: fields.tokuishitenCdTo,
      nonyuCdFrom: fields.nonyuCdFrom,
      nonyushitenCdFrom: fields.nonyushitenCdFrom,
      nonyuCdTo: fields.nonyuCdTo,
      nonyushitenCdTo: fields.nonyushitenCdTo,
    };
    B060030Print(param)
      .then((response) => {
        if (printed(response)) {
          setMsg(getClientMessage("B060030_001"));
        } else {
          download(response);
          setMsg(getClientMessage("B060030_002"));
        }
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  //印刷ボタンクリックイベント
  const handleClickPrintButton = () => {
    handlePrint();
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="受注生産品店別組立出荷リスト"
        gridTemplateColumns="6rem 6rem 5rem 1rem 9rem 1rem 6rem 1rem 6rem 5rem 16rem 4rem 12.4rem"
      >
        <SLabel>発行区分</SLabel>
        <SCodeSelect
          name="hakkoKbn"
          options={B060030HakkoKbn}
          {...register(1)}
          gridColumn="span 2"
        />
        <SLabel gridColumn="1">出荷日</SLabel>
        <SDateField name="shukaYmdFrom" {...register(2)} gridColumn="span 2" />
        <SText>～</SText>
        <SDateField name="shukaYmdTo" {...register(3)} />
        <SLabel gridColumn="1">製作日</SLabel>
        <SDateField
          name="seisakuYmdFrom"
          {...register(4)}
          gridColumn="span 2"
        />
        <SText>～</SText>
        <SDateField name="seisakuYmdTo" {...register(5)} />
        <SLabel gridColumn="1">受注No</SLabel>
        <SNumberField
          name="juchudenpyoNoFrom"
          code
          size={10}
          {...register(6)}
          gridColumn="span 2"
        />
        <SText>～</SText>
        <SNumberField name="juchudenpyoNoTo" code size={10} {...register(7)} />
        <SLabel gridColumn="1">加工種別</SLabel>
        <SNumberField
          name="kakoshubetsuCdFrom"
          code
          size={3}
          {...register(8)}
          dialog={<B000111 meishoKbn={meishoKbn.kakoshubetsu} />}
          handleSubmit={(data: any) => {
            methods.setValue("kakoshubetsuCdFrom", data.meishoCd);
            methods.setValue("kakoshubetsuNmFrom", data.meishoNm1);
          }}
          onBlur={() => B000111KakoshubetsuNmFrom(methods, navigate, setMsg)}
        />
        <STextField
          name="kakoshubetsuNmFrom"
          size={40}
          disabled
          gridColumn="span 5"
        />
        <SText>～</SText>
        <SNumberField
          name="kakoshubetsuCdTo"
          code
          size={3}
          {...register(9)}
          dialog={<B000111 meishoKbn={meishoKbn.kakoshubetsu} />}
          handleSubmit={(data: any) => {
            methods.setValue("kakoshubetsuCdTo", data.meishoCd);
            methods.setValue("kakoshubetsuNmTo", data.meishoNm1);
          }}
          onBlur={() => B000111KakoshubetsuNmTo(methods, navigate, setMsg)}
        />
        <STextField
          name="kakoshubetsuNmTo"
          size={40}
          disabled
          gridColumn="span 2"
        />
        <SLabel gridColumn="1">得意先</SLabel>
        <SNumberField
          name="tokuiCdFrom"
          onBlur={() => B000150TokuiNmFrom(methods, navigate, setMsg)}
          code
          size={6}
          {...register(10)}
        />
        <SNumberField
          name="tokuishitenCdFrom"
          code
          size={4}
          {...register(11)}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCdFrom", data.tokuiCd);
            methods.setValue("tokuishitenCdFrom", data.tokuishitenCd);
            methods.setValue("tokuiNmFrom", data.tokuiNmr);
          }}
          onBlur={() => B000150TokuiNmFrom(methods, navigate, setMsg)}
        />
        <STextField name="tokuiNmFrom" size={40} disabled gridColumn="span 4" />
        <SText>～</SText>
        <SNumberField
          name="tokuiCdTo"
          onBlur={() => B000150TokuiNmTo(methods, navigate, setMsg)}
          code
          size={6}
          {...register(12)}
        />
        <SNumberField
          name="tokuishitenCdTo"
          code
          size={4}
          {...register(13)}
          onBlur={() => B000150TokuiNmTo(methods, navigate, setMsg)}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCdTo", data.tokuiCd);
            methods.setValue("tokuishitenCdTo", data.tokuishitenCd);
            methods.setValue("tokuiNmTo", data.tokuiNmr);
          }}
        />
        <STextField name="tokuiNmTo" size={40} disabled />
        <SLabel gridColumn="1">納入先</SLabel>
        <SNumberField
          name="nonyuCdFrom"
          onBlur={() => B000151NonyuNmFrom(methods, navigate, setMsg)}
          code
          size={6}
          {...register(14)}
        />
        <SNumberField
          name="nonyushitenCdFrom"
          code
          size={4}
          {...register(15)}
          dialog={<B000151 />}
          handleSubmit={(data: any) => {
            methods.setValue("nonyuCdFrom", data.nonyuCd);
            methods.setValue("nonyushitenCdFrom", data.nonyushitenCd);
            methods.setValue("nonyuNmFrom", data.tokuiNmr);
          }}
          onBlur={() => B000151NonyuNmFrom(methods, navigate, setMsg)}
        />
        <STextField name="nonyuNmFrom" size={40} disabled gridColumn="span 4" />
        <SText>～</SText>
        <SNumberField
          name="nonyuCdTo"
          onBlur={() => B000151NonyuNmTo(methods, navigate, setMsg)}
          code
          size={6}
          {...register(16)}
        />
        <SNumberField
          name="nonyushitenCdTo"
          onBlur={() => B000151NonyuNmTo(methods, navigate, setMsg)}
          code
          size={4}
          {...register(17)}
          dialog={<B000151 />}
          handleSubmit={(data: any) => {
            methods.setValue("nonyuCdTo", data.nonyuCd);
            methods.setValue("nonyushitenCdTo", data.nonyushitenCd);
            methods.setValue("nonyuNmTo", data.tokuiNmr);
          }}
        />
        <STextField name="nonyuNmTo" size={40} disabled />
        <SFileButton
          label={labels.buttons.print}
          gridRow={8}
          gridColumn={12}
          {...register(18)}
          onClick={handleClickPrintButton}
          aligh="right"
        />
        <Box gridColumn={13} />
      </SFrame>
    </FormProvider>
  );
};
